/**
 * PreferencesSettingsPage - App-Wide Preferences
 *
 * Manages user preferences that affect app-wide behavior:
 * - Distance units (miles/kilometers)
 * - Future: Date format, time format, etc.
 *
 * Issue #1085: Moved from ProfileSettingsPage
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProfile, useUpdateProfile } from '../../hooks/useProfile';
import { TopBar, Button, Skeleton } from '../../components';
import { Ruler, Save, X, AlertCircle } from 'lucide-react';
import { ApiError } from '../../lib/api';

export default function PreferencesSettingsPage() {
  const navigate = useNavigate();
  const { data: profile, isLoading } = useProfile();
  const updateProfileMutation = useUpdateProfile();

  const [distanceUnit, setDistanceUnit] = useState<'mi' | 'km'>('mi');
  const [error, setError] = useState<string | null>(null);

  // React docs "adjust state on prop change" — initialize unit from profile
  // when it loads or changes.
  const [prevProfileUnit, setPrevProfileUnit] = useState(profile?.distance_unit);
  if (prevProfileUnit !== profile?.distance_unit) {
    setPrevProfileUnit(profile?.distance_unit);
    if (profile?.distance_unit) {
      setDistanceUnit(profile.distance_unit);
    }
  }

  // Derive hasChanges directly during render — no separate state needed.
  const hasChanges = profile ? distanceUnit !== (profile.distance_unit || 'mi') : false;

  const handleSave = async () => {
    setError(null);

    try {
      await updateProfileMutation.mutateAsync({
        distance_unit: distanceUnit,
      });
      navigate('/settings');
    } catch (err) {
      const message = err instanceof ApiError
        ? err.message
        : 'Failed to save preferences. Please try again.';
      setError(message);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Units & Display" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="px-4 py-6 space-y-6">
            <Skeleton className="h-32 w-full rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-oro-bg min-h-screen">
      <TopBar
        title="Units & Display"
        onBack={() => navigate(-1)}
      />

      <div className="flex-1 overflow-y-auto pt-14">
        <div className="p-4 space-y-6">

          {/* Distance Units Section */}
          <div className="bg-oro-surface border border-oro-border rounded-xl p-4">
            <h2 className="font-semibold text-text-primary mb-4 flex items-center gap-2">
              <Ruler className="w-5 h-5 text-accent" />
              Distance Units
            </h2>

            <div className="space-y-4">
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setDistanceUnit('mi')}
                  className={`flex-1 py-3 px-4 rounded-lg font-medium text-sm transition-all ${
                    distanceUnit === 'mi'
                      ? 'bg-accent/10 border-2 border-accent text-accent'
                      : 'bg-oro-surface-alt border border-oro-border text-text-secondary hover:border-oro-border-hover'
                  }`}
                >
                  Miles
                </button>
                <button
                  type="button"
                  onClick={() => setDistanceUnit('km')}
                  className={`flex-1 py-3 px-4 rounded-lg font-medium text-sm transition-all ${
                    distanceUnit === 'km'
                      ? 'bg-accent/10 border-2 border-accent text-accent'
                      : 'bg-oro-surface-alt border border-oro-border text-text-secondary hover:border-oro-border-hover'
                  }`}
                >
                  Kilometers
                </button>
              </div>
              <p className="text-xs text-text-muted">
                This affects how distances are displayed throughout the app.
              </p>
            </div>
          </div>

          {/* Future preferences placeholder - commented out until needed */}
          {/*
          <div className="bg-oro-surface border border-oro-border rounded-xl p-4">
            <h2 className="font-semibold text-text-primary mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-accent" />
              Date & Time
            </h2>
            <p className="text-sm text-text-muted">
              Additional display preferences coming soon.
            </p>
          </div>
          */}

          {/* Error Display */}
          {error && (
            <div className="p-4 rounded-lg bg-oro-error-soft border border-oro-error-border flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-oro-error shrink-0 mt-0.5" />
              <p className="text-sm text-oro-error">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3 pt-4">
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              onClick={handleSave}
              loading={updateProfileMutation.isPending}
              disabled={updateProfileMutation.isPending || !hasChanges}
            >
              <Save className="w-5 h-5 mr-2" />
              Save Changes
            </Button>

            <Button
              variant="secondary"
              size="lg"
              className="w-full"
              onClick={() => navigate('/settings')}
              disabled={updateProfileMutation.isPending}
            >
              <X className="w-5 h-5 mr-2" />
              Cancel
            </Button>
          </div>

        </div>
      </div>
    </div>
  );
}
